import type PopperJs from 'popper.js'
import { useCallback, useEffect, useRef } from 'react'

/**
 * Works around a Safari/iPad popper-positioning race.
 *
 * On WebKit the anchor's getBoundingClientRect is read before layout settles, so popper.js
 * paints once at viewport (0,0) — a flicker in the top-left corner — before recomputing against
 * the correct coords. Deferring one animation frame and forcing `popper.update()` lets layout
 * settle first. It's a no-op when the position was already correct (e.g. desktop).
 *
 * Returns a stable `popperRef` to pass to MUI's `PopperProps` and a stable `scheduleUpdate()` to
 * call when the popper opens (from `onOpen` for uncontrolled tooltips, or an effect on the `open`
 * prop for controlled ones). The pending frame is cancelled on unmount and re-scheduling, so an
 * update never fires against an unmounted Popper.
 */
export function usePopperPositionFix() {
  const popperRef = useRef<PopperJs | null>(null)
  const rafRef = useRef<number | null>(null)

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      popperRef.current?.update()
    })
  }, [])

  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    },
    []
  )

  return { popperRef, scheduleUpdate }
}
